import { useQuery } from '@tanstack/react-query';

import { buildEmlDocument } from './emlDocument';

interface EmlPreviewProps {
  /** Signed URL of the `.eml` file. */
  readonly src: string;
  readonly className?: string;
  readonly loading: React.ReactNode;
  readonly fallback: React.ReactNode;
}

const fetchEmlDocument = async (src: string): Promise<string> => {
  const response = await fetch(src);
  if (!response.ok) {
    throw new Error(`Email download failed with ${response.status}`);
  }
  // Loaded on demand: the parser is only needed once an email is opened, and
  // it builds a TextEncoder at import time, which jsdom does not provide.
  const { default: PostalMime } = await import('postal-mime');
  const email = await PostalMime.parse(await response.arrayBuffer(), {
    attachmentEncoding: 'base64',
  });
  return buildEmlDocument(email);
};

/**
 * A browser cannot render `message/rfc822`, so an `.eml` loaded straight into
 * an iframe downloads instead. This parses the message and renders it as a page.
 */
export const EmlPreview = ({
  src,
  className,
  loading,
  fallback,
}: EmlPreviewProps) => {
  const { data, isLoading, isError } = useQuery(
    ['eml-preview', src],
    () => fetchEmlDocument(src),
    { refetchOnWindowFocus: false, cacheTime: 0, retry: false }
  );

  if (isLoading) {
    return <>{loading}</>;
  }
  if (isError || !data) {
    return <>{fallback}</>;
  }
  return (
    <iframe
      title="Email preview"
      className={className}
      srcDoc={data}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
    />
  );
};
